<script lang="ts" setup>
import { ref, onMounted, reactive } from "vue";
import AUApi from "@/api";
import { mountedBase, onSave, BaseSetup, onDelete } from "@/DataLoaderStatic";

import { getKennungValueDisplay, getKennungTitle, getKennungValue } from "@/DataHelper";
import { goBack } from "@/NavigationHelper";
import EditTimePicker from "@/editor/EditTimePicker.vue";

const emit = defineEmits<{
  back: [];
  save: [];
  calculated: [data?: any];
}>();
const props = defineProps<{
  zeitbereichUebergabe: APIZeitbereich;
  showClose?: boolean;
}>();
let anzeige = ref<boolean>(true);
let anzeigeAntrag = ref<boolean>(true);
let deleteVisible = ref<boolean>(false);
let saveVisible = ref<boolean>(true);
let showUestAuszahlen = ref<boolean>(false);
let showauszahlungUest = ref<boolean>(false);
let showZulage1: string = "";
let showZulage2: string = "";
let showZulage3: string = "";
let showCloseMutable = ref<boolean>(false);

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_WF_Antrag_Tag",
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  pageParams.zeitbereich = props.zeitbereichUebergabe;
  showCloseMutable.value = props.showClose;

  await mountedBase(uiData, pageParams, false);
  if (!uiData.showLoading && uiData.data) {
    if (uiData.data && uiData.data[0].aic_Bew_pool) {
      deleteVisible.value = true;
    }
    const keinAntrag = getKennungValueDisplay(
      uiData.data[0],
      "keinAntrag",
      uiData.columns
    );

    const editierbar = getKennungValue(uiData.data[0], "ZE_ANTRAG_BOOL3", uiData.columns);
    if (editierbar === 1) {
      saveVisible.value = true;
      deleteVisible.value = false;
    }
    if (keinAntrag === "1.00") {
      anzeigeAntrag.value = false;
    }
    const uestAuszahlen = getKennungTitle("ZE_UEST_NICHT_AUSZAH", uiData.columns);
    if (uestAuszahlen) {
      showUestAuszahlen.value = true;
    }
    const auszahlungUest = getKennungTitle("ZE_AUSZAHLUNG_UEST", uiData.columns);
    if (auszahlungUest) {
      showauszahlungUest.value = true;
    }
    showZulage1 = getKennungTitle("Zulage_1", uiData.columns);
    showZulage2 = getKennungTitle("Zulage_2", uiData.columns);
    showZulage3 = getKennungTitle("Zulage_3", uiData.columns);
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
async function bewDelete() {
  if (uiData.data && uiData.data[0].aic_Bew_pool) {
    await onDelete(
      uiData,
      pageParams,
      uiData.data[0].aic_Bew_pool,
      "",
      pageParams.zeitbereich
    );
    emit("back");
  }
}
async function save() {
  await onSave(uiData, pageParams, 0, "");
  emit("back");
}
</script>

<template>
  <v-sheet class="pa-0">
    <v-progress-linear
      :indeterminate="true"
      v-if="uiData.showLoading"
      color="dvhSlider"
    ></v-progress-linear>
    <v-expansion-panels class="pa-0" rounded="0">
      <v-expansion-panel v-if="uiData.initialized">
        <v-expansion-panel-title
          disable-icon-rotate
          class="subtitle-1 lighten-2 white--text dvhBackground"
        >
          <span style="color: white">{{ uiData.header.bezeichnung }}</span>
          <span style="color: white" v-if="anzeigeAntrag"
            >:
            {{
              getKennungValueDisplay(uiData.data[0], "ZE_ANTRAG_BOOL3", uiData.columns)
            }}</span
          >
          <template v-slot:actions>
            <v-icon color="white">$expand</v-icon>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="compact-inputs">
          <v-row v-if="anzeige">
            <v-col class="colNoPad" cols="12" sm="6">
              <EditCheckBox kennung="ZE_KEINE_VERFALLBEGI" />
            </v-col>

            <v-col class="colNoPad" cols="12" sm="6">
              <EditCheckBox kennung="ZE_K_VERFALLZEITENDE" />
            </v-col>
          </v-row>
          <v-row v-if="anzeige && (showUestAuszahlen || showauszahlungUest)">
            <v-col class="colNoPad" cols="12" sm="6" v-if="showUestAuszahlen">
              <EditCheckBox kennung="ZE_UEST_NICHT_AUSZAH" />
            </v-col>
            <v-col class="colNoPad" cols="12" sm="6" v-if="showauszahlungUest">
              <EditTimePicker kennung="ZE_AUSZAHLUNG_UEST" />
            </v-col>
          </v-row>
          <v-row v-if="anzeige && showZulage1">
            <v-col class="colNoPad" cols="12" sm="6">
              <v-sheet grow pa-1>
                <div>
                  {{ showZulage1 }}:
                  {{ getKennungValueDisplay(uiData.data[0], "Zulage_1", uiData.columns) }}
                </div>
              </v-sheet>
            </v-col>

            <v-col v-if="showZulage2" class="colNoPad" cols="12" xs="12">
              <v-sheet grow pa-1>
                <div>
                  {{ showZulage2 }}:
                  {{ getKennungValueDisplay(uiData.data[0], "Zulage_2", uiData.columns) }}
                </div>
              </v-sheet>
            </v-col>
            <v-col v-if="showZulage3" class="colNoPad" cols="12" xs="12">
              <v-sheet grow pa-1>
                <div>
                  {{ showZulage3 }}:
                  {{ getKennungValueDisplay(uiData.data[0], "Zulage_3", uiData.columns) }}
                </div>
              </v-sheet>
            </v-col>
          </v-row>
          <!-- <EditTextArea
            clearable="false"
            v-if="anzeige"
            kennung="MEMO_MITARBEITER"
          />
          <EditTextArea
            v-if="!anzeige"
            :disabled="true"
            kennung="MEMO_MITARBEITER"
          /> -->
          <!-- <EditTextArea kennung="VG" /> -->
          <v-row v-if="anzeige" class="mt-0">
            <v-col class="colNoPad" cols="12" sm="6">
              <EditCheckBox kennung="ZE_KEINE_PAUSE" :autofocus="true" />
            </v-col>
            <v-col class="colNoPad" cols="12" sm="6">
              <EditTimePicker kennung="ZE_ALTERNATIVE_PAUSE" />
            </v-col>
          </v-row>
          <v-row v-if="!anzeige">
            <v-col class="colNoPad" cols="12" sm="6">
              <EditCheckBox :disabled="true" kennung="ZE_KEINE_PAUSE" />

              <EditCheckBox :disabled="true" kennung="ZE_KEINE_VERFALLBEGI" />
              <EditCheckBox :disabled="true" kennung="ZE_K_VERFALLZEITENDE" />
            </v-col>
            <v-col class="py-0" cols="12" sm="6">
              <EditTimePicker :disabled="true" kennung="ZE_ALTERNATIVE_PAUSE" />
              <EditCheckBox
                v-if="showUestAuszahlen"
                :disabled="true"
                kennung="ZE_UEST_NICHT_AUSZAH"
              />
              <EditTimePicker
                v-if="showauszahlungUest"
                :disabled="true"
                kennung="ZE_AUSZAHLUNG_UEST"
              />
            </v-col>
          </v-row>
          <v-row class="mt-0" v-if="anzeige">
            <v-col class="colNoPad" cols="12" sm="12">
              <EditTextArea clearable="false" kennung="MEMO_MITARBEITER" />
            </v-col>
          </v-row>
          <v-row class="mt-0" v-if="!anzeige">
            <v-col class="colNoPad" cols="12" sm="12">
              <EditTextArea :disabled="true" kennung="MEMO_MITARBEITER" />
            </v-col>
          </v-row>
          <v-row class="mt-0">
            <v-col class="colNoPad" cols="12" sm="12">
              <EditTextArea kennung="VG" />
            </v-col>
          </v-row>
          <v-row>
            <v-spacer></v-spacer>
            <CommonBaseButtons
              :saveVisible="saveVisible"
              :backVisible="showCloseMutable"
              :deleteVisible="deleteVisible"
              :createVisible="false"
              :refreshVisible="false"
              @back="goBack()"
              @delete="bewDelete"
              @save="save"
            ></CommonBaseButtons>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-sheet>
</template>

<style scoped>
.dvhCol {
  padding-right: 0px;
  padding-left: 10px;
  padding-top: 15px;
  padding-bottom: 0px;
}
.colNoPad {
  padding: 0 0 0 0;
}
/* Eingabe-Komponenten (Checkbox, TimePicker, TextArea, etc.) auf 13px */
.compact-inputs :deep(.v-input),
.compact-inputs :deep(.v-field__input),
.compact-inputs :deep(.v-field__label),
.compact-inputs :deep(.v-label),
.compact-inputs :deep(.v-checkbox .v-label),
.compact-inputs :deep(.v-textarea .v-field__input),
.compact-inputs :deep(.v-select .v-field__input) {
  font-size: 13px;
  line-height: 1.3;
}
</style>
